import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Check, Camera } from 'lucide-react';
import { API, useAuth } from '../App';

// Fixed 4x6" print layout: two 2x6" strips side-by-side, each with 4 photos
const FIXED_LAYOUT = {
  printWidth: 1200, // 4" at 300dpi
  printHeight: 1800, // 6" at 300dpi
  stripWidth: 600, // 2" at 300dpi
  stripHeight: 1800, // 6" at 300dpi
  photoSlots: [
    { id: 1, x: 30, y: 30, width: 540, height: 360 },
    { id: 2, x: 30, y: 410, width: 540, height: 360 },
    { id: 3, x: 30, y: 790, width: 540, height: 360 },
    { id: 4, x: 30, y: 1170, width: 540, height: 360 },
  ],
  brandingArea: { y: 1550, height: 220 }, // Bottom area for logos
};

// Resolve a stored logo URL for browser display.
// Backend may return either:
//   - an absolute URL (S3/CloudFront): "https://d1234.cloudfront.net/logos/.../x.png"
//   - a relative path (local fallback): "/api/uploads/logos/x.png"
// For absolute URLs we use them as-is. For relative paths we prepend the
// backend host (REACT_APP_BACKEND_URL stripped of its trailing /api).
const resolveLogoSrc = (url) => {
  if (!url) return '';
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const apiHost = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
  return `${apiHost}${url.startsWith('/') ? '' : '/'}${url}`;
};

const SimpleTemplateEditor = ({ kioskId, initialConfig, onSave }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [backgroundColor, setBackgroundColor] = useState(initialConfig?.backgroundColor || '#FFFFFF');
  const [customLogoUrl, setCustomLogoUrl] = useState(initialConfig?.customLogoUrl || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Scale factor for preview (fit in viewport)
  const previewScale = 0.25;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setCustomLogoUrl(response.data.url);
    } catch (err) {
      console.error('Failed to upload logo:', err);
    }
    setUploading(false);
  };

  const handleRemoveLogo = () => {
    setCustomLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/kiosks/${kioskId}/template-config`, {
        backgroundColor,
        customLogoUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      if (onSave) {
        onSave({ backgroundColor, customLogoUrl });
      }
    } catch (err) {
      console.error('Failed to save template config:', err);
    }
    setSaving(false);
  };

  // Render the template preview
  const renderStripPreview = () => {
    const scale = previewScale;
    return (
      <div 
        className="relative mx-auto"
        style={{ 
          width: FIXED_LAYOUT.stripWidth * scale,
          height: FIXED_LAYOUT.stripHeight * scale,
          backgroundColor: backgroundColor,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
        }}
      >
        {/* Photo slots */}
        {FIXED_LAYOUT.photoSlots.map((slot, index) => (
          <div
            key={slot.id}
            className="absolute border-2 border-dashed border-rose-300 bg-rose-50/50 flex items-center justify-center"
            style={{
              left: slot.x * scale,
              top: slot.y * scale,
              width: slot.width * scale,
              height: slot.height * scale,
            }}
          >
            <div className="text-center">
              <Camera className="w-4 h-4 mx-auto text-rose-400" />
              <span className="text-[8px] text-rose-400 font-medium">Photo {index + 1}</span>
            </div>
          </div>
        ))}

        {/* Branding area */}
        <div
          className="absolute left-0 right-0 flex items-center justify-center"
          style={{
            top: FIXED_LAYOUT.brandingArea.y * scale,
            height: FIXED_LAYOUT.brandingArea.height * scale,
          }}
        >
          {/* Custom logo - large and centered */}
          {customLogoUrl && (
            <img 
              src={resolveLogoSrc(customLogoUrl)} 
              alt="Custom Logo" 
              className="object-contain"
              style={{ 
                maxHeight: FIXED_LAYOUT.brandingArea.height * scale * 0.8, 
                maxWidth: FIXED_LAYOUT.stripWidth * scale * 0.9 
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="simple-template-editor">
      {/* Preview Section */}
      <div className="card-dashboard">
        <h3 className="font-serif text-lg text-charcoal-900 mb-4">Print Preview</h3>
        <p className="text-charcoal-800/60 text-sm mb-6">
          Fixed 4x6" print layout: Two identical 2x6" strips, each with 4 photos
        </p>
        
        {/* 4x6 Print Preview (showing both strips) */}
        <div 
          className="bg-gray-100 rounded-lg p-4 flex justify-center items-center"
          style={{ minHeight: FIXED_LAYOUT.stripHeight * previewScale + 40 }}
        >
          <div className="flex gap-1 border-2 border-dashed border-gray-300 p-1 rounded bg-white">
            {/* Left strip */}
            <div className="relative">
              {renderStripPreview()}
              <div className="absolute -bottom-5 left-0 right-0 text-center">
                <span className="text-[10px] text-charcoal-800/40">Strip 1</span>
              </div>
            </div>
            
            {/* Divider */}
            <div className="w-px bg-gray-300 self-stretch" />
            
            {/* Right strip (identical) */}
            <div className="relative">
              {renderStripPreview()}
              <div className="absolute -bottom-5 left-0 right-0 text-center">
                <span className="text-[10px] text-charcoal-800/40">Strip 2</span>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-xs text-charcoal-800/40 text-center mt-6">
          Customer receives 2 identical strips per print
        </p>
      </div>

      {/* Customization Options */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Background Color */}
        <div className="card-dashboard">
          <h3 className="font-serif text-lg text-charcoal-900 mb-4">Background Color</h3>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="w-16 h-16 rounded-lg cursor-pointer border-2 border-charcoal-900/10"
              data-testid="template-bg-color"
            />
            <div className="flex-1">
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                placeholder="#FFFFFF"
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl font-mono text-sm"
                data-testid="template-bg-hex"
              />
              <p className="text-xs text-charcoal-800/40 mt-2">Enter a hex code or use the color picker</p>
            </div>
          </div>
        </div>

        {/* Custom Logo Upload */}
        <div className="card-dashboard">
          <h3 className="font-serif text-lg text-charcoal-900 mb-4">Your Brand Logo</h3>
          <p className="text-charcoal-800/60 text-sm mb-4">
            Upload your logo to appear at the bottom of each strip
          </p>
          
          {customLogoUrl ? (
            <div className="flex items-center gap-4 p-4 bg-cream-50 rounded-xl">
              <img 
                src={resolveLogoSrc(customLogoUrl)}
                alt="Custom Logo"
                className="h-16 max-w-[160px] object-contain"
              />
              <button
                onClick={handleRemoveLogo}
                className="ml-auto p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="remove-logo-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-charcoal-900/20 rounded-xl p-8 text-center hover:border-charcoal-900/40 hover:bg-cream-50 transition-colors">
                {uploading ? (
                  <div className="animate-pulse">
                    <div className="w-8 h-8 mx-auto mb-2 bg-charcoal-900/20 rounded-full" />
                    <span className="text-charcoal-800/60">Uploading...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto mb-2 text-charcoal-800/40" />
                    <span className="text-charcoal-800/60">Click to upload your logo</span>
                    <p className="text-xs text-charcoal-800/40 mt-1">PNG, JPG up to 2MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
            </label>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2 min-w-[160px] justify-center disabled:opacity-50"
          data-testid="save-template-config-btn"
        >
          {saveSuccess ? (
            <>
              <Check className="w-4 h-4" />
              Saved!
            </>
          ) : saving ? (
            'Saving...'
          ) : (
            'Save Template'
          )}
        </button>
      </div>
    </div>
  );
};

export default SimpleTemplateEditor;
